import { useState, useEffect, useCallback } from "react";

import { useAppNavigate } from "contexts";
import { useUniqueId } from "hooks";
import debounce from "lodash/debounce";
import _omit from "lodash/omit";
import queryString from "query-string";
import { useLocation } from "react-router-dom";
import { windowSearchLocationToObject } from "utils";

const useSyncStateWithURL = <T extends object>(
  initialState: T,
  config?: {
    mode: "onChange" | "onSubmit";
    exclude?: (keyof T)[];
    disabledLoadOnMount?: boolean;
  }
): {
  state: T;
  updateState: (newState: T, callback?: () => void, timeout?: number) => void;
  reset: () => void;
  debounceUpdateState: (e: T) => void;
  queryKey: number;
} => {
  const [queryKey, generateQueryKey] = useUniqueId();
  const { push } = useAppNavigate();
  const location = useLocation();
  const searchLocation = windowSearchLocationToObject();
  const [initDone, setInitDone] = useState(false);
  const [state, setState] = useState<T>({
    ...initialState,
    ...(searchLocation as T),
  });
  const { mode = "onSubmit", disabledLoadOnMount } = config ?? {};

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlState = params.toString();

    if (!initDone) {
      const parsedState = queryString.parse(urlState) as T;
      if (typeof parsedState === "object" && !initDone) {
        setState((prev) => ({ ...prev, ...parsedState }));
        setInitDone(true);
        if (!disabledLoadOnMount) {
          generateQueryKey();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const updateState = (newState: T, callback?: () => void, timeout = 100) => {
    let newStateUpdated = {
      ...state,
      ...newState,
    };

    // When change filter, reset page to 1
    if (
      mode === "onChange" &&
      !Object.keys(newState as Record<string, unknown>)?.includes("page")
    ) {
      newStateUpdated = {
        ...newStateUpdated,
        page: 1,
      };
    }

    // Exclude some keys
    if (config?.exclude?.length) {
      setState(newStateUpdated);
      newStateUpdated = _omit(newStateUpdated, config.exclude) as T;
    }

    setState((prev) => ({ ...prev, ...newStateUpdated }));
    generateQueryKey();

    push({
      to: location.pathname,
      params: queryString.stringify(newStateUpdated as Record<string, unknown>),
    });

    if (typeof callback === "function" && callback) {
      setTimeout(() => {
        callback();
      }, timeout);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceUpdateState = useCallback(debounce(updateState, 1000), [
    updateState,
    debounce,
  ]);

  const reset = () => {
    updateState(initialState);
    generateQueryKey();
  };

  return {
    state,
    queryKey,
    updateState,
    debounceUpdateState,
    reset,
  };
};

export default useSyncStateWithURL;
